import { useState, useCallback, useRef, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import {
  XMarkIcon,
  UserPlusIcon,
  EnvelopeIcon,
  UserIcon,
  ShareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@/components/common/Button'
import { AgentManagementService } from '@/services/api/services/AgentManagementService'
import { useAgentSharing } from '@/hooks/useAgentSharing'
import type { Agent } from '@/services/api/models/Agent'
import type { InviteUserRequest } from '@/services/api/models/InviteUserRequest'
import type { PermissionResponse } from '@/services/api/models/PermissionResponse'
import type { InvitationResponse } from '@/services/api/models/InvitationResponse'
import { clsx } from 'clsx'

interface ShareAgentModalProps {
  isOpen: boolean
  onClose: () => void
  agent: Agent | null
  onInvitationSent?: () => void
}

type InviteType = 'email' | 'username'
type PermissionLevel = 'read' | 'write'

export function ShareAgentModal({
  isOpen,
  onClose,
  agent,
  onInvitationSent
}: ShareAgentModalProps) {
  const [inviteType, setInviteType] = useState<InviteType>('email')
  const [recipient, setRecipient] = useState('')
  const [permission, setPermission] = useState<PermissionLevel>('read')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [currentPermissions, setCurrentPermissions] = useState<PermissionResponse[]>([])
  const [pendingInvitations, setPendingInvitations] = useState<InvitationResponse[]>([])
  const [fetchingData, setFetchingData] = useState(false)

  const modalRef = useRef<HTMLDivElement>(null)
  const firstInputRef = useRef<HTMLInputElement>(null)
  
  const { getAgentPermissions, getOutgoingInvitations } = useAgentSharing()

  // Reset form and fetch data when modal opens/closes
  useEffect(() => {
    if (isOpen && agent?.id) {
      setRecipient('')
      setPermission('read')
      setErrors({})
      setCurrentPermissions([])
      setPendingInvitations([])
      
      // Fetch current permissions and pending invitations
      const fetchAgentData = async () => {
        setFetchingData(true)
        try {
          const [permissions, invitations] = await Promise.all([
            getAgentPermissions(agent.id!),
            getOutgoingInvitations(agent.id!)
          ])
          setCurrentPermissions(permissions)
          setPendingInvitations(invitations)
        } catch (error) {
          console.error('Failed to fetch agent data:', error)
        } finally {
          setFetchingData(false)
        }
      }
      
      fetchAgentData()
      
      // Focus the first input after a short delay
      setTimeout(() => {
        firstInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen, agent?.id, getAgentPermissions, getOutgoingInvitations])

  // Handle escape key and backdrop clicks
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {}

    if (!recipient.trim()) {
      newErrors.recipient = `${inviteType === 'email' ? 'Email' : 'Username'} is required`
    } else if (inviteType === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(recipient)) {
        newErrors.recipient = 'Please enter a valid email address'
      } else {
        // Check if email already has access
        const hasExistingPermission = currentPermissions.some(
          permission => permission.user_email?.toLowerCase() === recipient.toLowerCase()
        )
        if (hasExistingPermission) {
          newErrors.recipient = 'This user already has access to this agent'
        } else {
          // Check if email already has pending invitation
          const hasPendingInvitation = pendingInvitations.some(
            invitation => invitation.invitee_email?.toLowerCase() === recipient.toLowerCase()
          )
          if (hasPendingInvitation) {
            newErrors.recipient = 'This user already has a pending invitation for this agent'
          }
        }
      }
    } else {
      // Username validation
      if (recipient.length < 3) {
        newErrors.recipient = 'Username must be at least 3 characters long'
      } else if (!/^[a-zA-Z0-9_-]+$/.test(recipient)) {
        newErrors.recipient = 'Username can only contain letters, numbers, underscores, and hyphens'
      } else {
        // Check if username already has access
        const hasExistingPermission = currentPermissions.some(
          permission => permission.username?.toLowerCase() === recipient.toLowerCase()
        )
        if (hasExistingPermission) {
          newErrors.recipient = 'This user already has access to this agent'
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [recipient, inviteType, currentPermissions, pendingInvitations])

  const handleSendInvitation = async () => {
    if (!agent?.id) return

    if (!validateForm()) return

    setIsLoading(true)

    try {
      const requestBody: InviteUserRequest = {
        permission,
        ...(inviteType === 'email' 
          ? { email: recipient } 
          : { username: recipient })
      }

      await AgentManagementService.inviteUserToAgent({
        agentId: agent.id,
        requestBody
      })

      toast.success(`Invitation sent to ${recipient}`)
      onInvitationSent?.()
      onClose()
    } catch (error: unknown) {
      console.error('Failed to send invitation:', error)
      
      // Extract server error message if available
      let serverErrorMessage = 'Failed to send invitation. Please try again.'
      if (error && typeof error === 'object' && 'body' in error && error.body && 
          typeof error.body === 'object' && 'message' in error.body) {
        serverErrorMessage = String(error.body.message)
      } else if (error instanceof Error) {
        serverErrorMessage = error.message
      }
      
      // Handle specific error messages from the API
      if (serverErrorMessage.includes('Cannot invite yourself')) {
        setErrors({ recipient: 'You cannot invite yourself' })
      } else if (serverErrorMessage.includes('User already has a pending invitation')) {
        setErrors({ recipient: 'This user already has a pending invitation for this agent' })
      } else if (serverErrorMessage.includes('User already has access')) {
        setErrors({ recipient: 'This user already has access to this agent' })
      } else if (serverErrorMessage.includes('User not found') || serverErrorMessage.includes('user not found')) {
        // Use the actual server error message instead of hardcoded text
        setErrors({ recipient: serverErrorMessage })
      } else {
        toast.error(serverErrorMessage)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleModalClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  if (!isOpen || !agent) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700"
        onClick={handleModalClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/20">
              <ShareIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Share Agent
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Invite others to access "{agent.name}"
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Invite Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              How would you like to invite them?
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => {
                  setInviteType('email')
                  setRecipient('')
                  setErrors({})
                }}
                className={clsx(
                  'flex items-center space-x-3 p-3 rounded-lg border transition-all',
                  inviteType === 'email'
                    ? 'border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                )}
              >
                <EnvelopeIcon className="w-5 h-5" />
                <span className="text-sm font-medium">By Email</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setInviteType('username')
                  setRecipient('')
                  setErrors({})
                }}
                className={clsx(
                  'flex items-center space-x-3 p-3 rounded-lg border transition-all',
                  inviteType === 'username'
                    ? 'border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                )}
              >
                <UserIcon className="w-5 h-5" />
                <span className="text-sm font-medium">By Username</span>
              </button>
            </div>
          </div>

          {/* Recipient Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {inviteType === 'email' ? 'Email Address' : 'Username'}
            </label>
            <input
              ref={firstInputRef}
              type={inviteType === 'email' ? 'email' : 'text'}
              value={recipient}
              onChange={(e) => {
                setRecipient(e.target.value)
                // Clear errors when user starts typing to trigger real-time validation
                setErrors({ ...errors, recipient: '' })
              }}
              className={clsx(
                'w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                errors.recipient
                  ? 'border-red-300 dark:border-red-600'
                  : 'border-gray-300 dark:border-gray-600'
              )}
              placeholder={
                inviteType === 'email'
                  ? 'colleague@example.com'
                  : 'username'
              }
            />
            {errors.recipient && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors.recipient}
              </p>
            )}
          </div>

          {/* Permission Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Permission Level
            </label>
            <div className="space-y-3">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  value="read"
                  checked={permission === 'read'}
                  onChange={(e) => { const v = e.target.value; setPermission(v === 'read' ? 'read' : 'write') }}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Read Access
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Can view agent details, and create datasources uses the agent.
                  </div>
                </div>
              </label>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  value="write"
                  checked={permission === 'write'}
                  onChange={(e) => { const v = e.target.value; setPermission(v === 'write' ? 'write' : 'read') }}
                  className="mt-1 h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Write Access
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    All read permissions plus ability to edit agent, delete the agent, share the agent with other users, and even remove the owner.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">What happens next?</p>
                <ul className="space-y-1 text-xs">
                  <li>• The user will receive an invitation notification</li>
                  <li>• They can accept or decline the invitation</li>
                  <li>• Once accepted, the agent will appear in their agents list</li>
                  <li>• You can manage their permissions anytime from the agent settings</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendInvitation}
            disabled={isLoading || fetchingData}
            className="flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <UserPlusIcon className="w-4 h-4" />
                <span>Send Invitation</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}